import { MOMENT_TYPES } from 'utils/constants';
import { MONTHLY_TREE_CHILD_INDENT_PX } from './monthlyLayoutConstants';
import type { MonthlyTimelineItem } from './types';

const MANUALLY_EXPANDED_KEY = 'manually_expanded_cards';
const MANUALLY_COLLAPSED_KEY = 'manually_collapsed_cards';

export const MONTHLY_CHILD_ROW_HEIGHT_PX = 20;
export const MONTHLY_FEATURE_ROW_HEIGHT_PX = 18;
export const MONTHLY_FEATURE_ROW_GAP_PX = 6;
export const MONTHLY_EXPANDED_TREE_GAP_PX = 6;
/** Space below expanded content; paired with TRACK_GAP_PX for card-to-card spacing. */
export const MONTHLY_EXPANDED_BLOCK_TRAILING_PX = 0;
export const MONTHLY_FEATURE_CARD_WIDTH_PX = 305;

export function getMonthlyRegularChildren(item: MonthlyTimelineItem) {
  const children = Array.isArray(item.childRelationships)
    ? item.childRelationships
    : [];

  return children.filter(child => child.projectType !== MOMENT_TYPES.FEATURE);
}

export function getMonthlyFeatures(item: MonthlyTimelineItem) {
  const fromFeatures = Array.isArray(item.features) ? item.features : [];
  const fromChildren = Array.isArray(item.childRelationships)
    ? item.childRelationships.filter(
        child => child.projectType === MOMENT_TYPES.FEATURE
      )
    : [];

  const seen = new Set<string>();
  return [...fromFeatures, ...fromChildren].filter(feature => {
    const key = feature.featureId || feature.issueId || feature.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function isMonthlyItemExpandable(item: MonthlyTimelineItem) {
  return (
    getMonthlyRegularChildren(item).length > 0 ||
    getMonthlyFeatures(item).length > 0
  );
}

export function getMonthlyExpandedChildCount(item: MonthlyTimelineItem) {
  return (
    getMonthlyRegularChildren(item).length + getMonthlyFeatures(item).length
  );
}

export function getMonthlyBaseExpandKey(item: MonthlyTimelineItem): string {
  const issueId = item.issueId || item.id;
  if (item.releasePhaseId) {
    return `${issueId}-${item.releasePhaseId}-${Boolean(item.showOnTimeline)}`;
  }
  return issueId;
}

export function getMonthlyMustWinInstanceKey(
  mustWinRowKey: string,
  item: MonthlyTimelineItem
): string {
  return `${mustWinRowKey}-${getMonthlyBaseExpandKey(item)}`;
}

export function getMonthlyExpandKey(
  item: MonthlyTimelineItem,
  mustWinInstanceKey?: string
): string {
  return mustWinInstanceKey ?? getMonthlyBaseExpandKey(item);
}

export function getManuallyExpandedCards(): string[] {
  if (typeof sessionStorage === 'undefined') return [];

  try {
    return JSON.parse(
      sessionStorage.getItem(MANUALLY_EXPANDED_KEY) || '[]'
    ) as string[];
  } catch {
    return [];
  }
}

export function setManuallyExpandedCard(expandKey: string, expanded: boolean) {
  const expandedCards = getManuallyExpandedCards();

  if (expanded) {
    if (!expandedCards.includes(expandKey)) {
      sessionStorage.setItem(
        MANUALLY_EXPANDED_KEY,
        JSON.stringify([...expandedCards, expandKey])
      );
    }
    return;
  }

  sessionStorage.setItem(
    MANUALLY_EXPANDED_KEY,
    JSON.stringify(expandedCards.filter(id => id !== expandKey))
  );
}

export function getManuallyCollapsedCards(): string[] {
  if (typeof sessionStorage === 'undefined') return [];

  try {
    return JSON.parse(
      sessionStorage.getItem(MANUALLY_COLLAPSED_KEY) || '[]'
    ) as string[];
  } catch {
    return [];
  }
}

export function setManuallyCollapsedCard(
  expandKey: string,
  collapsed: boolean
) {
  const collapsedCards = getManuallyCollapsedCards();

  if (collapsed) {
    if (!collapsedCards.includes(expandKey)) {
      sessionStorage.setItem(
        MANUALLY_COLLAPSED_KEY,
        JSON.stringify([...collapsedCards, expandKey])
      );
    }
    return;
  }

  sessionStorage.setItem(
    MANUALLY_COLLAPSED_KEY,
    JSON.stringify(collapsedCards.filter(id => id !== expandKey))
  );
}

export function clearMonthlyManualExpandState() {
  if (typeof sessionStorage === 'undefined') return;

  sessionStorage.removeItem(MANUALLY_EXPANDED_KEY);
  sessionStorage.removeItem(MANUALLY_COLLAPSED_KEY);
}

export function shouldUnnestMonthlyItem(
  item: MonthlyTimelineItem,
  unnestParentIds: string[]
) {
  if (!item.issueId) return false;

  if (unnestParentIds.includes(item.issueId)) return true;

  if (item.releasePhaseId) {
    const composite = `${item.issueId}-${item.releasePhaseId}-${Boolean(
      item.showOnTimeline
    )}`;
    if (unnestParentIds.includes(composite)) return true;
  }

  return false;
}

export function resolveMonthlyItemExpanded(
  item: MonthlyTimelineItem,
  {
    isCollapsed,
    isUnNested,
    unnestParentIds,
    mustWinInstanceKey
  }: {
    isCollapsed: boolean;
    isUnNested: boolean;
    unnestParentIds: string[];
    mustWinInstanceKey?: string;
  }
): boolean {
  if (!isMonthlyItemExpandable(item)) return false;

  const expandKey = getMonthlyExpandKey(item, mustWinInstanceKey);
  const isManuallyExpanded = getManuallyExpandedCards().includes(expandKey);
  const isManuallyCollapsed = getManuallyCollapsedCards().includes(expandKey);

  if (isManuallyExpanded) return true;
  if (isManuallyCollapsed) return false;

  const isFilterMode = unnestParentIds.length > 0;

  if (isFilterMode) {
    return shouldUnnestMonthlyItem(item, unnestParentIds);
  }

  if (isCollapsed && isUnNested) return true;
  if (isCollapsed) return false;

  return isUnNested;
}

function getExpandedColumnHeightPx(item: MonthlyTimelineItem): number {
  const regularCount = getMonthlyRegularChildren(item).length;
  const featureCount = getMonthlyFeatures(item).length;

  const regularHeight =
    regularCount > 0
      ? regularCount * MONTHLY_CHILD_ROW_HEIGHT_PX +
        Math.max(0, regularCount - 1) * MONTHLY_EXPANDED_TREE_GAP_PX
      : 0;

  const featureHeight =
    featureCount > 0
      ? featureCount * MONTHLY_FEATURE_ROW_HEIGHT_PX +
        Math.max(0, featureCount - 1) * MONTHLY_FEATURE_ROW_GAP_PX
      : 0;

  let contentHeight = 0;

  if (regularHeight > 0 && featureHeight > 0) {
    contentHeight =
      regularHeight + MONTHLY_EXPANDED_TREE_GAP_PX + featureHeight;
  } else {
    contentHeight = regularHeight + featureHeight;
  }

  if (contentHeight === 0) {
    return 0;
  }

  return contentHeight + MONTHLY_EXPANDED_BLOCK_TRAILING_PX;
}

export function getMonthlyItemBlockHeightPx(
  item: MonthlyTimelineItem,
  isExpanded: boolean
): number {
  if (!isExpanded || getMonthlyExpandedChildCount(item) === 0) {
    return 20;
  }

  return 20 + MONTHLY_EXPANDED_TREE_GAP_PX + getExpandedColumnHeightPx(item);
}

export function getMonthlyExpandedBlockWidthPx(
  item: MonthlyTimelineItem,
  laneLabel: string,
  rootWidth: number,
  estimateChildWidth: (child: MonthlyTimelineItem) => number,
  estimateFeatureWidth?: (feature: MonthlyTimelineItem) => number
): number {
  const regularChildren = getMonthlyRegularChildren(item);
  const features = getMonthlyFeatures(item);

  if (!regularChildren.length && !features.length) {
    return rootWidth;
  }

  const childWidth =
    regularChildren.length > 0
      ? Math.max(...regularChildren.map(child => estimateChildWidth(child))) +
        MONTHLY_TREE_CHILD_INDENT_PX
      : 0;

  const featureWidth = features.length
    ? Math.max(
        ...features.map(feature =>
          estimateFeatureWidth
            ? estimateFeatureWidth(feature)
            : MONTHLY_FEATURE_CARD_WIDTH_PX
        )
      ) + MONTHLY_TREE_CHILD_INDENT_PX
    : 0;

  return Math.max(rootWidth, childWidth, featureWidth);
}
